import React, { useState } from 'react';
import { GUIDANCE_STYLE, SEVERITY_STYLE, TONE_STYLE, CALL_DISCLAIMER } from '../../shared/callHighlightStyles';
import { formatEventDate, isSameEarningsEvent } from '../../utils/earningsElapsed';

/**
 * Earnings-call highlights, generated from the call AUDIO.
 *
 * This is the single renderer for both surfaces — the earnings-calendar tile and the expanded
 * stock row. Any second surface that re-implemented this would drift the moment the extraction
 * schema changed, and two panels disagreeing about the same call reads as a bug.
 *
 * Styling is inline on purpose: several Tailwind utilities are simply absent from this repo's
 * prebuilt `tailwind-output.css` and fail silently, and Bootstrap's CDN spacing utilities are
 * `!important` and outrank Tailwind by name.
 *
 * Sizing note: desktop renders under `body { zoom: 0.9 }`, so every size here is ~10% smaller on
 * screen than its declared value. These are set against the rendered result, not the raw number.
 *
 * The verbatim transcript is intentionally NOT rendered or linked. It is kept worker-side purely
 * so extraction can be re-run without re-paying for speech-to-text.
 */

const LABEL = {
  fontSize: 12,
  color: '#6b7280',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 5,
};

const BODY = { fontSize: 14.5, lineHeight: 1.6, color: '#374151' };
const META = { fontSize: 11.5, color: '#9ca3af' };

const formatCallDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;

/**
 * How the market took the print. Same green/red pairs as the guidance and tone pills so one row of
 * badges reads as one palette; `flat` is reserved for an exact zero, which is a real statement
 * ("unchanged") and not the same thing as a missing number.
 */
const REACTION_STYLE = {
  up: { bg: '#dcfce7', fg: '#166534' },
  down: { bg: '#fee2e2', fg: '#991b1b' },
  flat: { bg: '#f1f5f9', fg: '#475569' },
};

/**
 * Absence must not become a number. `Number(null)` is 0 and FINITE, so a bare isFinite check turns
 * a missing percent into a confident "0.00%" — a real claim ("unchanged") invented out of nothing.
 */
const percentOrNull = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const signed = (value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

/**
 * How the market took the call, in the one pill the panel has room for.
 *
 * Two different measurements answer this, and which one is available depends only on how long ago
 * the call was — so they are ranked rather than shown together:
 *
 *   1. THE SETTLED REACTION — close after the report versus the close before it, off one
 *      `earnings_history` row via workers/shared/earningsReaction.js. It is the real answer, but it
 *      cannot exist until the next session settles and earnings_cron records it (22:30 UTC, #248).
 *      It must also belong to THIS call: those two rows are written by different workers on
 *      different schedules, so the newest reaction is the previous quarter's until the recorder
 *      catches up (isSameEarningsEvent).
 *   2. THE AFTER-HOURS MOVE — captured the evening of the report by earnings-transcript-cron and
 *      stored on the call row (#250), because the live column it comes from is overwritten within
 *      the day. Measured against the same baseline as the settled figure, the close before the
 *      report, so it is an early read of the same quantity rather than a different one.
 *
 * The settled number therefore SUPERSEDES the after-hours one the moment it lands, which is the
 * intended behaviour and not a value changing under the reader: the label says which is on screen.
 *
 * Both are distinct from the table's "Since Earnings" drift (live quote versus the close after).
 */
const buildReaction = (reaction, afterHours, callDate) => {
  const settled = percentOrNull(reaction?.percent);
  if (settled !== null && isSameEarningsEvent(reaction.date, callDate)) {
    const reportedOn = formatEventDate(reaction.date);
    return {
      style: REACTION_STYLE[settled > 0 ? 'up' : settled < 0 ? 'down' : 'flat'],
      text: `${signed(settled)} on the report`,
      title: `Close after the report vs the close before it${reportedOn ? ` (${reportedOn})` : ''}`,
    };
  }

  // No date check here: the worker only records a capture whose print belongs to this call's
  // session, so a stored value is already tied to the call by construction.
  const ah = percentOrNull(afterHours?.percent);
  if (ah === null) return null;
  const price = percentOrNull(afterHours.price);
  return {
    style: REACTION_STYLE[ah > 0 ? 'up' : ah < 0 ? 'down' : 'flat'],
    text: `${signed(ah)} after hours`,
    title: `After-hours move against the close before the report${price !== null ? ` — $${price.toFixed(2)}` : ''}`
      + '. Updates to the full reaction once the next session closes.',
  };
};

// Exported for the stock table's Guidance cell (issue #306), so the two pills cannot drift apart.
export const Pill = ({ bg, fg, children }) => (
  <span
    style={{
      display: 'inline-block',
      background: bg,
      color: fg,
      fontSize: 12,
      fontWeight: 600,
      padding: '3px 10px',
      borderRadius: 11,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

/** A titled block of "lead — detail" lines. Renders nothing when the model returned no entries. */
const Section = ({ title, items, leadKey, detailKey, leadColor = '#111827' }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={LABEL}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => {
          const lead = item[leadKey];
          const detail = item[detailKey];
          if (!lead && !detail) return null;
          return (
            <div key={i} style={BODY}>
              {lead && <span style={{ color: leadColor, fontWeight: 600 }}>{lead}</span>}
              {lead && detail ? ' — ' : ''}
              {detail}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Teaser shown to non-power users. The worker sends a `locked` sentinel in the same map shape as a
 * real record, so this component owns the whole permission branch and the five parent components
 * that thread highlights down stay unaware of tiers.
 */
const LockedTeaser = ({ callDate, compact }) => {
  const label = formatCallDate(callDate);
  return (
    <div
      style={{
        border: '1px dashed #d1d5db',
        borderRadius: 8,
        background: '#fafafa',
        padding: compact ? 11 : 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span style={{ ...LABEL, marginBottom: 0 }}>Earnings Call Highlights</span>
        <Pill bg="#ede9fe" fg="#5b21b6">Pro</Pill>
        {label && <span style={META}>{label}</span>}
      </div>
      <div style={{ ...BODY, color: '#6b7280', marginTop: 7 }}>
        This call has been transcribed from the audio. Pro accounts see the developments,
        product updates, growth drivers, ranked threats and the analyst Q&amp;A.
      </div>
    </div>
  );
};

const CallHighlights = ({ highlights, reaction, compact = false }) => {
  // Collapsed by default: this panel already lives inside an expanded row/tile, and the full
  // breakdown runs long enough to bury the news and AI panels beneath it.
  const [open, setOpen] = useState(false);

  if (!highlights) return null;
  if (highlights.locked) return <LockedTeaser callDate={highlights.callDate} compact={compact} />;

  const {
    headline,
    guidance,
    developments = [],
    products = [],
    growth = [],
    growth_drivers: drivers = [],
    threats = [],
    market_dynamics: marketDynamics = [],
    competition = [],
    analyst_qa: analystQa = [],
    tone,
    watch_items: watchItems = [],
    callDate,
    audioMinutes,
    afterHours,
  } = highlights;

  const guidanceStyle = GUIDANCE_STYLE[guidance?.direction];
  const toneStyle = TONE_STYLE[tone?.label];
  const callDateLabel = formatCallDate(callDate);
  const reactionPill = buildReaction(reaction, afterHours, callDate);

  // Shown on the collapsed row so the toggle advertises what is behind it rather than being a
  // blind "show more".
  const contents = [
    developments.length && `${developments.length} developments`,
    threats.length && `${threats.length} threats`,
    products.length && `${products.length} products`,
    analystQa.length && `${analystQa.length} Q&A`,
  ].filter(Boolean);

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        background: '#fff',
        padding: compact ? 11 : 14,
      }}
    >
      {/* ── Always-visible summary ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, marginBottom: 7 }}>
        <span style={{ ...LABEL, marginBottom: 0 }}>Earnings Call Highlights</span>
        {callDateLabel && <span style={META}>{callDateLabel}</span>}
        {audioMinutes ? <span style={META}>· {audioMinutes} min call</span> : null}
      </div>

      {headline && (
        <div style={{ fontSize: 16, fontWeight: 600, color: '#111827', lineHeight: 1.4, marginBottom: 8 }}>
          {headline}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
        {guidanceStyle && <Pill bg={guidanceStyle.bg} fg={guidanceStyle.fg}>{guidanceStyle.text}</Pill>}
        {toneStyle && <Pill bg={toneStyle.bg} fg={toneStyle.fg}>{tone.label}</Pill>}
        {/* What the call was worth: the pills above are what the company SAID, this is what the
            market paid for it. It sits last so the row reads said-then-priced. */}
        {reactionPill && (
          <span title={reactionPill.title}>
            <Pill bg={reactionPill.style.bg} fg={reactionPill.style.fg}>{reactionPill.text}</Pill>
          </span>
        )}
      </div>
      {guidance?.detail && <div style={{ ...BODY, marginTop: 7 }}>{guidance.detail}</div>}

      {/* ── Toggle ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          marginTop: 10,
          padding: '5px 10px',
          fontSize: 12.5,
          fontWeight: 600,
          color: '#374151',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
        {open ? 'Hide breakdown' : 'Full breakdown'}
        {!open && contents.length > 0 && (
          <span style={{ fontWeight: 400, color: '#9ca3af' }}>· {contents.join(' · ')}</span>
        )}
      </button>

      {/* ── Collapsible detail ── */}
      {open && (
        <div>
          <Section title="Developments" items={developments} leadKey="what" detailKey="why_it_matters" />
          {/* Products lead with WHAT THE THING IS, because a bare product name tells a reader who
              does not follow the company nothing (issue #215). `what_it_is` is absent on records
              extracted before that field existed, so the gloss and its parentheses are rendered
              only when present — no dangling punctuation on older rows. */}
          {products.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={LABEL}>Products</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {products.map((p, i) => (
                  <div key={i} style={BODY}>
                    <span style={{ color: '#111827', fontWeight: 600 }}>{p.name}</span>
                    {p.what_it_is && <span style={{ color: '#6b7280' }}>{` (${p.what_it_is})`}</span>}
                    {p.update ? ` — ${p.update}` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Growth keeps the rate visually distinct from the area it describes. */}
          {growth.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={LABEL}>Growth</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {growth.map((g, i) => (
                  <div key={i} style={BODY}>
                    <span style={{ fontWeight: 600, color: '#111827' }}>{g.area}</span>
                    {g.rate && <span style={{ color: '#059669', fontWeight: 600 }}>{` ${g.rate}`}</span>}
                    {g.detail ? ` — ${g.detail}` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Section title="Growth drivers" items={drivers} leadKey="factor" detailKey="evidence" leadColor="#047857" />

          {/* Threats arrive pre-sorted by severity from the worker, so the largest is always first. */}
          {threats.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={LABEL}>Threats to growth</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {threats.map((t, i) => {
                  const sev = SEVERITY_STYLE[t.severity];
                  return (
                    <div key={i} style={BODY}>
                      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        {sev && <Pill bg={sev.bg} fg={sev.fg}>{t.severity}</Pill>}
                        <span style={{ color: '#b91c1c', fontWeight: 600 }}>{t.threat}</span>
                      </span>
                      {t.evidence ? ` — ${t.evidence}` : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Section title="Market dynamics" items={marketDynamics} leadKey="dynamic" detailKey="what_was_said" />
          <Section title="Competitive environment" items={competition} leadKey="competitor_or_dynamic" detailKey="what_was_said" />

          {/* ── Analyst Q&A ── */}
          {analystQa.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={LABEL}>Analyst Q&amp;A</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {analystQa.map((qa, i) => (
                  <div key={i} style={BODY}>
                    {/* Attributed by FIRM, not by person (issue #215): the institution asking is
                        the signal, while the individual's name means nothing to most readers. The
                        extracted `analyst` name is kept in the record for fidelity — and is what
                        makes the Whisper roster useful — but is not surfaced. */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#111827' }}>
                        {qa.firm ? `${qa.firm} analyst` : 'Analyst'}
                      </span>
                      {qa.evasive === true && <Pill bg="#fef3c7" fg="#92400e">deflected</Pill>}
                    </div>
                    {qa.asked && <div style={{ color: '#6b7280' }}>Q: {qa.asked}</div>}
                    {qa.answered && <div>A: {qa.answered}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {watchItems.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={LABEL}>Watch next quarter</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {watchItems.map((w, i) => (
                  <li key={i} style={BODY}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ ...META, marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
            {CALL_DISCLAIMER}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallHighlights;
